import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { compose } from 'recompose';

import logger from 'js/app/loggerSingleton';
import user from 'js/lib/user';
import waitFor from 'js/lib/waitFor';

import { ACCUSED_KEYS } from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/constants/PlagiarismFlagConstants';
import type Team from 'bundles/author-assignment-grading/components/common/types/Team';
import {
  getGuiltyMembersFromCheatingCaseComments,
  mapCommentsToCheatingCases,
} from 'bundles/author-assignment-grading/utils/plagiarismFlagUtils';
import withPlagiarismPermissions from 'bundles/authoring/common/utils/permissions/withPlagiarismPermissions';
import type GroupMember from 'bundles/authoring/groups/models/GroupMember';
import { useNaptime } from 'bundles/naptimejs';
import type {
  CheatingCaseRuling,
  CheatingCaseStaffComment,
} from 'bundles/naptimejs/resources/cheatingCaseStaffComments.v1';
import CheatingCaseStaffCommentsV1, {
  CHEATING_CASE_RULINGS,
} from 'bundles/naptimejs/resources/cheatingCaseStaffComments.v1';

// eslint-disable-next-line @typescript-eslint/no-empty-function
async function noop() {}

export const PlagiarismContext = React.createContext<PlagiarismData>({
  cheatingCaseStaffComments: [],
  cheatingCaseCommentsByCase: {},
  canStaffComment: false,
  saving: false,
  fetching: false,
  saveData: noop,
  fetchRuling: noop,
  fetchCanStaffComment: noop,
  guiltyMembers: [],
  activeIntegrityFlagCount: 0,
  setActiveIntegrityFlagCount: noop,
});

export type Props = {
  courseId: string;
  courseBranchId: string;
  itemId: string;
  userId: string;
  team?: Team;
  groupId?: string;
  submissionKey?: string | null;
  isAnonymized?: boolean | null;
  learners?: Array<GroupMember>;
  canUpdatePlagiarismFlag?: boolean;
  propsLoading?: boolean;
  children: React.ReactNode;
};

export type PlagiarismData = Pick<
  Props,
  'team' | 'submissionKey' | 'groupId' | 'isAnonymized' | 'learners' | 'canUpdatePlagiarismFlag'
> & {
  cheatingCaseStaffComments: CheatingCaseStaffComment[];
  cheatingCaseCommentsByCase: CommentDictionary;
  ruling?: CheatingCaseRuling;
  guiltyMembers: number[];
  canStaffComment: boolean;
  saving: boolean;
  fetching: boolean;
  saveData(data: SaveData): Promise<void>;
  fetchRuling(): Promise<void>;
  fetchCanStaffComment(): Promise<void>;
  activeIntegrityFlagCount: number;
  setActiveIntegrityFlagCount: React.Dispatch<React.SetStateAction<number>>;
};

type CommentDictionary = { [caseId: string]: CheatingCaseStaffComment[] };

type NaptimeData = {
  cheatingCaseStaffComments?: CheatingCaseStaffComment[];
};

export type SaveData = {
  guiltyMembers?: Array<number>;
  ruling?: CheatingCaseRuling;
  comment: string;
};

export function PlagiarismProviderComponent(props: Props) {
  const {
    children,
    team,
    submissionKey,
    groupId,
    itemId,
    courseId,
    courseBranchId,
    userId,
    isAnonymized,
    learners,
    canUpdatePlagiarismFlag,
    propsLoading = false,
  } = props;

  const [canStaffComment, setCanStaffComment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [ruling, setRuling] = useState(CHEATING_CASE_RULINGS.UNRESOLVED);

  const [cheatingCaseCommentsByCase, setCheatingCaseCommentsByCase] = useState<CommentDictionary>({});
  const [cheatingCaseStaffComments, setCheatingCaseStaffComments] = useState<CheatingCaseStaffComment[]>([]);
  const [activeIntegrityFlagCount, setActiveIntegrityFlagCount] = useState(0);

  const naptimeResult = useNaptime<NaptimeData>(() => {
    if (propsLoading) {
      return {};
    }
    if (team) {
      return {
        cheatingCaseStaffComments: CheatingCaseStaffCommentsV1.byTeamCourseBranchAndItem({
          scopedTeamId: team.id,
          itemId,
          courseId,
          branchId: courseBranchId,
        }),
      };
    } else {
      return {
        cheatingCaseStaffComments: CheatingCaseStaffCommentsV1.byUserCourseBranchAndItem({
          userId,
          itemId,
          courseId,
          branchId: courseBranchId,
        }),
      };
    }
  }, [propsLoading, userId, team, itemId, courseId, courseBranchId]);

  const { executeMutation } = naptimeResult.naptime;

  // Naptime returns `cheatingCaseStaffComments=undefined` while the request is
  // `pending`. When a consumer adds a new comment, `PlagiarismProvider`
  // executes mutation on `CheatingCaseStaffCommentsV1` resource. This action
  // triggers data re-fetch from the resource, which results in `pending=true`
  // and `cheatingCaseStaffComments=undefined`. To avoid temporarily unavailable
  // comments, we keep comments in state and update when `pending` changes.
  useEffect(() => {
    if (!naptimeResult.pending) {
      const comments = naptimeResult.data.cheatingCaseStaffComments || [];
      setCheatingCaseCommentsByCase(mapCommentsToCheatingCases(comments));
      setCheatingCaseStaffComments(comments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naptimeResult.pending]);

  const fetchRuling = async () => {
    setFetching(true);

    return executeMutation(
      CheatingCaseStaffCommentsV1.resolutionByAccusedCourseBranchAndItem({
        userId,
        teamId: team && team.id,
        itemId,
        courseId,
        branchId: courseBranchId,
      })
    )
      .then(({ data }: $TSFixMe) => {
        const newRuling = data && data.ruling;
        setRuling(newRuling);
        setFetching(false);
      })
      .catch((error) => {
        setRuling(CHEATING_CASE_RULINGS.UNRESOLVED);
        setFetching(false);
        logger.error(error);
      });
  };

  const fetchCanStaffComment = async () => {
    setFetching(true);

    return executeMutation(
      CheatingCaseStaffCommentsV1.canStaffComment({
        userId,
        teamId: team && team.id,
        itemId,
        courseId,
        branchId: courseBranchId,
      })
    )
      .then(({ data }: $TSFixMe) => {
        setCanStaffComment(data);
        setFetching(false);
      })
      .catch((error) => {
        setCanStaffComment(false);
        setFetching(false);
        logger.error(error);
      });
  };

  const saveData = async (data: SaveData) => {
    const accused = team
      ? {
          [ACCUSED_KEYS.team]: {
            teamId: team.id,
            guiltyMembers: data.guiltyMembers,
          },
        }
      : {
          [ACCUSED_KEYS.user]: {
            userId: parseInt(userId, 10),
          },
        };

    const body = {
      accused,
      submissionKey,
      comment: data.comment,
      author: `USER~${user.get().id}`,
      courseItemContainer: {
        courseId,
        itemId,
        branchId: courseBranchId,
      },
      ruling: data.ruling ? { ruling: data.ruling } : {},
    };

    setSaving(true);
    await executeMutation(CheatingCaseStaffCommentsV1.create(body));
    await fetchRuling();
    setSaving(false);
  };

  useEffect(() => {
    if (propsLoading) {
      return;
    }
    Promise.all([fetchRuling(), fetchCanStaffComment()]).then(() => setFetching(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsLoading, props.userId, props.team?.id, props.itemId, props.courseId, props.courseBranchId]);

  const guiltyMembers = useMemo(
    () => getGuiltyMembersFromCheatingCaseComments(cheatingCaseCommentsByCase) || (team && team.members),
    [team, cheatingCaseCommentsByCase]
  );

  return (
    <PlagiarismContext.Provider
      value={{
        fetching,
        team,
        cheatingCaseCommentsByCase,
        cheatingCaseStaffComments,
        submissionKey,
        groupId,
        isAnonymized,
        learners,
        saving,
        ruling,
        canStaffComment,
        canUpdatePlagiarismFlag,
        saveData,
        fetchCanStaffComment,
        fetchRuling,
        guiltyMembers,
        activeIntegrityFlagCount,
        setActiveIntegrityFlagCount,
      }}
    >
      {children}
    </PlagiarismContext.Provider>
  );
}

export default compose<Props, Props>(
  waitFor(({ userId, team }) => userId || team),
  withPlagiarismPermissions(({ courseId }) => ({ courseId }))
)(PlagiarismProviderComponent);
